'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Question {
  id: number;
  question: string;
  options: string[];
}

interface AssessmentToolProps {
  questions: Question[];
}

const AssessmentTool = ({ questions }: AssessmentToolProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [showResults, setShowResults] = useState<boolean>(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg p-6 shadow-subtle border border-border">
        <div className="h-8 bg-muted/50 rounded animate-pulse mb-4"></div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-12 bg-muted/50 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  const handleAnswer = (optionIndex: number) => {
    setAnswers({ ...answers, [currentQuestion]: optionIndex });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
  };

  const calculateScore = () => {
    const totalAnswered = Object.keys(answers).length;
    const score = (totalAnswered / questions.length) * 100;
    return score;
  };

  const getRecommendation = () => {
    const score = calculateScore();
    if (score >= 80) {
      return {
        level: 'Advanced',
        color: 'text-success',
        message: 'Your business shows strong digital maturity. Consider our Enterprise solutions for optimization and scaling.',
        services: ['Custom Solution Development', 'AI Implementation & Integration']
      };
    } else if (score >= 50) {
      return {
        level: 'Intermediate',
        color: 'text-warning',
        message: 'Your business has good foundations. Our Professional services can help accelerate your digital transformation.',
        services: ['Business Process Optimization', 'Marketing Strategy & Automation']
      };
    } else {
      return {
        level: 'Beginner',
        color: 'text-accent',
        message: 'Great opportunity for growth! Our Basic services provide the perfect starting point for your digital journey.',
        services: ['Digital Transformation Roadmaps', 'Marketing Strategy & Automation']
      };
    }
  };

  if (showResults) {
    const recommendation = getRecommendation();
    return (
      <div className="bg-card rounded-lg p-6 shadow-subtle border border-border">
        <div className="text-center mb-6">
          <Icon name="CheckBadgeIcon" size={64} className="text-success mx-auto mb-4" />
          <h3 className="text-2xl font-display font-bold text-foreground mb-2">
            Assessment Complete!
          </h3>
          <p className="text-muted-foreground">
            Based on your responses, here's our recommendation
          </p>
        </div>

        <div className="bg-muted rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-muted-foreground">Digital Maturity Level</span>
            <span className={`text-2xl font-display font-bold ${recommendation.color}`}>
              {recommendation.level}
            </span>
          </div>
          <div className="w-full bg-background rounded-full h-3 mb-4">
            <div
              className="bg-primary h-3 rounded-full transition-all duration-500 ease-brand"
              style={{ width: `${calculateScore()}%` }}
            ></div>
          </div>
          <p className="text-sm text-foreground">{recommendation.message}</p>
        </div>

        <div className="mb-6">
          <h4 className="text-lg font-display font-bold text-foreground mb-3">
            Recommended Services
          </h4>
          <ul className="space-y-2">
            {recommendation.services.map((service, index) => (
              <li key={index} className="flex items-start space-x-2 text-sm text-foreground">
                <Icon name="ArrowRightIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>{service}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleRestart}
            className="flex-1 px-4 py-3 border border-border rounded-md text-sm font-semibold text-foreground hover:bg-muted transition-all duration-300 ease-brand"
          >
            Retake Assessment
          </button>
          <a href="/contact" className="flex-1 px-4 py-3 bg-accent text-accent-foreground rounded-md text-sm font-semibold text-center hover:bg-accent/90 transition-all duration-300 ease-brand">
            Request Quote
          </a>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="bg-card rounded-lg p-6 shadow-subtle border border-border">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <span className="text-sm font-medium text-primary">{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-background rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300 ease-brand"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      <h3 className="text-xl font-display font-bold text-foreground mb-4">
        {questions[currentQuestion].question}
      </h3>

      <div className="space-y-3 mb-6">
        {questions[currentQuestion].options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleAnswer(index)}
            className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-300 ease-brand ${
              answers[currentQuestion] === index
                ? 'border-primary bg-primary/10' :'border-border bg-background hover:border-primary/50'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  answers[currentQuestion] === index
                    ? 'border-primary bg-primary' :'border-border'
                }`}
              >
                {answers[currentQuestion] === index && (
                  <Icon name="CheckIcon" size={12} className="text-primary-foreground" />
                )}
              </div>
              <span className="text-sm text-foreground">{option}</span>
            </div>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
          className="px-4 py-2 border border-border rounded-md text-sm font-semibold text-foreground hover:bg-muted transition-all duration-300 ease-brand disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={handleNext}
          disabled={answers[currentQuestion] === undefined}
          className="px-6 py-2 bg-primary text-primary-foreground rounded-md text-sm font-semibold hover:bg-primary/90 transition-all duration-300 ease-brand disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {currentQuestion === questions.length - 1 ? 'View Results' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default AssessmentTool;